import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, BRAND_LOGO, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const ROLE_LABELS = {
  buyer: { label: "Buyer", blurb: "I need parts for my vehicle." },
  mechanic: { label: "Mechanic", blurb: "I run a workshop and buy often." },
  seller: { label: "Seller / Vendor", blurb: "I supply auto parts." },
  local_agent: { label: "Local Agent", blurb: "I verify parts and users in my city." },
};

export default function Register() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") || "buyer");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("Kenya");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { email, password, name, role, country, phone });
      setUser(data);
      toast.success(`Welcome, ${data.name}!`);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-xl bg-white border-2 border-[#121212] p-8">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <img src={BRAND_LOGO} alt="" className="h-12 w-12 object-contain" />
          <div className="leading-none">
            <div className="font-display font-extrabold text-sm">AUTO PARTS</div>
            <div className="font-display font-extrabold text-sm text-[#EF3340]">AFRICA</div>
          </div>
        </Link>
        <h1 className="font-display text-3xl font-extrabold mb-2">Create your account</h1>
        <p className="text-sm text-zinc-600 mb-6">Join the continent's auto parts marketplace.</p>

        <button onClick={googleLogin} className="w-full border-2 border-[#121212] py-3 font-semibold uppercase tracking-wider text-sm hover:bg-[#121212] hover:text-white transition-colors flex items-center justify-center gap-3 mb-5" data-testid="register-google">
          Sign up with Google
        </button>
        <div className="flex items-center gap-3 mb-5 text-xs text-zinc-400 uppercase tracking-wider">
          <div className="flex-1 h-px bg-[#E4E4E7]" />
          <span>or email</span>
          <div className="flex-1 h-px bg-[#E4E4E7]" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">I am a…</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ROLE_LABELS).map(([key, val]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRole(key)}
                  className={`text-left p-3 border-2 transition-all ${role === key ? "border-[#009A44] bg-[#009A44]/5" : "border-[#E4E4E7] hover:border-[#121212]"}`}
                  data-testid={`role-${key}`}
                >
                  <div className="font-bold text-sm">{val.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{val.blurb}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Full name</label>
              <input required value={name} onChange={(e) => setName(e.target.value)} className="input-apa" data-testid="register-name" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Country</label>
              <input required value={country} onChange={(e) => setCountry(e.target.value)} className="input-apa" data-testid="register-country" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-apa" data-testid="register-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-apa" data-testid="register-password" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">Phone (opt.)</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-apa" data-testid="register-phone" />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full !py-3.5" data-testid="register-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Create account
          </button>
        </form>
        <p className="text-sm text-zinc-600 mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="font-bold text-[#009A44] hover:underline">Log in</Link>
        </p>
      </div>
    </main>
  );
}
